import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import type { CustomCellEditorProps } from 'ag-grid-react'
import CtxDimensionTreeSelect from '../../../../../features/dimension/components/CtxDimensionTreeSelect'
import './SelectCellEditor.css'

/**
 * Dimension TreeSelect Cell Editor
 *
 * Wraps CtxDimensionTreeSelect for ag-grid cell editing.
 * Uses a popup container with mousedown prevention to keep focus in the grid cell.
 */
const DimensionTreeSelectCellEditor = memo(
  ({ value, onValueChange, stopEditing, cellStartedEdit }: CustomCellEditorProps<any, string>) => {
    const popupContainerRef = useRef<HTMLDivElement | null>(null)
    const [isReady, setIsReady] = useState(false)

    useEffect(() => {
      setTimeout(() => {
        setIsReady(true)
      }, 0)
    }, [])

    // Cleanup popup container on unmount
    useEffect(() => {
      return () => {
        if (popupContainerRef.current) {
          document.body.removeChild(popupContainerRef.current)
          popupContainerRef.current = null
        }
      }
    }, [])

    const getPopupContainer = useCallback(() => {
      if (!popupContainerRef.current) {
        popupContainerRef.current = document.createElement('div')
        popupContainerRef.current.addEventListener('mousedown', e => {
          e.preventDefault()
        })
        document.body.appendChild(popupContainerRef.current)
      }
      return popupContainerRef.current
    }, [])

    const handleChange = (newValue: string) => {
      onValueChange(newValue)
      if (cellStartedEdit) {
        setTimeout(() => {
          stopEditing()
        }, 0)
      }
    }

    if (!isReady) return null

    return (
      <CtxDimensionTreeSelect
        value={value}
        onChange={handleChange}
        style={{ width: '100%' }}
        getPopupContainer={getPopupContainer}
      />
    )
  }
)

DimensionTreeSelectCellEditor.displayName = 'DimensionTreeSelectCellEditor'

export default DimensionTreeSelectCellEditor
